import { supabase } from '../lib/supabase';

export const alertService = {
  // Get all active alerts with filtering
  async getAlerts(filters = {}) {
    try {
      let query = supabase
        ?.from('project_alerts')
        ?.select(`
          *,
          project:chat_projects(id, name),
          acknowledged_by_user:user_profiles!project_alerts_acknowledged_by_fkey(id, full_name),
          resolved_by_user:user_profiles!project_alerts_resolved_by_fkey(id, full_name),
          assignments:alert_agent_assignments(
            id,
            assignment_type,
            assigned_at,
            accepted_at,
            completed_at,
            agent:agents(id, name, role, status)
          )
        `)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.urgency) {
        query = query?.eq('urgency', filters?.urgency);
      }
      if (filters?.alertType) {
        query = query?.eq('alert_type', filters?.alertType);
      }
      if (filters?.projectId) {
        query = query?.eq('project_id', filters?.projectId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(alert => ({
        id: alert?.id,
        projectId: alert?.project_id,
        projectName: alert?.project?.name,
        alertType: alert?.alert_type,
        urgency: alert?.urgency,
        status: alert?.status,
        title: alert?.title,
        description: alert?.description,
        metricName: alert?.metric_name,
        metricValue: alert?.metric_value,
        thresholdValue: alert?.threshold_value,
        triggeredCondition: alert?.triggered_condition,
        metadata: alert?.metadata,
        createdAt: alert?.created_at,
        acknowledgedAt: alert?.acknowledged_at,
        acknowledgedBy: alert?.acknowledged_by_user?.full_name,
        resolvedAt: alert?.resolved_at,
        resolvedBy: alert?.resolved_by_user?.full_name,
        escalationTimer: alert?.escalation_timer,
        escalationCount: alert?.escalation_count,
        assignments: alert?.assignments?.map(a => ({
          id: a?.id,
          assignmentType: a?.assignment_type,
          assignedAt: a?.assigned_at,
          acceptedAt: a?.accepted_at,
          completedAt: a?.completed_at,
          agent: {
            id: a?.agent?.id,
            name: a?.agent?.name,
            role: a?.agent?.role,
            status: a?.agent?.status
          }
        })) || []
      })) || [];
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  },

  // Get alert statistics
  async getAlertStats() {
    try {
      const { data: alerts, error } = await supabase
        ?.from('project_alerts')
        ?.select('urgency, status, alert_type');

      if (error) throw error;

      const stats = {
        total: alerts?.length || 0,
        byUrgency: {
          critical: alerts?.filter(a => a?.urgency === 'critical')?.length || 0,
          high: alerts?.filter(a => a?.urgency === 'high')?.length || 0,
          medium: alerts?.filter(a => a?.urgency === 'medium')?.length || 0,
          low: alerts?.filter(a => a?.urgency === 'low')?.length || 0
        },
        byStatus: {
          active: alerts?.filter(a => a?.status === 'active')?.length || 0,
          acknowledged: alerts?.filter(a => a?.status === 'acknowledged')?.length || 0,
          assigned: alerts?.filter(a => a?.status === 'assigned')?.length || 0,
          resolved: alerts?.filter(a => a?.status === 'resolved')?.length || 0,
          escalated: alerts?.filter(a => a?.status === 'escalated')?.length || 0
        },
        byType: alerts?.reduce((acc, alert) => {
          acc[alert?.alert_type] = (acc?.[alert?.alert_type] || 0) + 1;
          return acc;
        }, {})
      };

      return stats;
    } catch (error) {
      console.error('Error fetching alert stats:', error);
      throw error;
    }
  },

  // Create new alert
  async createAlert(alertData) {
    try {
      const { data, error } = await supabase
        ?.from('project_alerts')
        ?.insert({
          project_id: alertData?.projectId,
          alert_type: alertData?.alertType,
          urgency: alertData?.urgency,
          title: alertData?.title,
          description: alertData?.description,
          metric_name: alertData?.metricName,
          metric_value: alertData?.metricValue,
          threshold_value: alertData?.thresholdValue,
          triggered_condition: alertData?.triggeredCondition,
          metadata: alertData?.metadata || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating alert:', error);
      throw error;
    }
  },

  // Acknowledge alert
  async acknowledgeAlert(alertId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('project_alerts')
        ?.update({
          status: 'acknowledged',
          acknowledged_at: new Date()?.toISOString(),
          acknowledged_by: userId
        })
        ?.eq('id', alertId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  },

  // Resolve alert
  async resolveAlert(alertId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('project_alerts')
        ?.update({
          status: 'resolved',
          resolved_at: new Date()?.toISOString(),
          resolved_by: userId
        })
        ?.eq('id', alertId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error resolving alert:', error);
      throw error;
    }
  },

  // Get workflow routing rules
  async getRoutingRules() {
    try {
      const { data, error } = await supabase
        ?.from('workflow_routing_rules')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('priority_score', { ascending: false });

      if (error) throw error;

      return data?.map(rule => ({
        id: rule?.id,
        ruleName: rule?.rule_name,
        alertType: rule?.alert_type,
        urgencyLevel: rule?.urgency_level,
        targetAgentRole: rule?.target_agent_role,
        conditionType: rule?.condition_type,
        priorityScore: rule?.priority_score,
        triggerConditions: rule?.trigger_conditions,
        isActive: rule?.is_active,
        createdAt: rule?.created_at,
        updatedAt: rule?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching routing rules:', error);
      throw error;
    }
  },

  // Create routing rule
  async createRoutingRule(ruleData) {
    try {
      const { data, error } = await supabase
        ?.from('workflow_routing_rules')
        ?.insert({
          rule_name: ruleData?.ruleName,
          alert_type: ruleData?.alertType,
          urgency_level: ruleData?.urgencyLevel,
          target_agent_role: ruleData?.targetAgentRole,
          condition_type: ruleData?.conditionType || 'automatic',
          priority_score: ruleData?.priorityScore || 50,
          trigger_conditions: ruleData?.triggerConditions || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating routing rule:', error);
      throw error;
    }
  },

  // Update routing rule
  async updateRoutingRule(ruleId, updates) {
    try {
      const updateData = {};
      if (updates?.ruleName) updateData.rule_name = updates?.ruleName;
      if (updates?.priorityScore !== undefined) updateData.priority_score = updates?.priorityScore;
      if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;
      if (updates?.triggerConditions) updateData.trigger_conditions = updates?.triggerConditions;
      updateData.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('workflow_routing_rules')
        ?.update(updateData)
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating routing rule:', error);
      throw error;
    }
  },

  // Get agent workload
  async getAgentWorkload() {
    try {
      const { data, error } = await supabase
        ?.from('agent_workload')
        ?.select(`
          *,
          agent:agents(id, name, role, status, specialization)
        `)
        ?.order('active_alerts_count', { ascending: false });

      if (error) throw error;

      return data?.map(workload => ({
        id: workload?.id,
        agentId: workload?.agent_id,
        agentName: workload?.agent?.name,
        agentRole: workload?.agent?.role,
        agentStatus: workload?.agent?.status,
        specialization: workload?.agent?.specialization,
        activeAlertsCount: workload?.active_alerts_count,
        totalAssignmentsToday: workload?.total_assignments_today,
        avgResolutionTimeMinutes: workload?.avg_resolution_time_minutes,
        lastAssignmentAt: workload?.last_assignment_at,
        updatedAt: workload?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching agent workload:', error);
      throw error;
    }
  },

  // Manually assign alert to agent
  async manuallyAssignAlert(alertId, agentId) {
    try {
      // Create assignment
      const { data: assignment, error: assignError } = await supabase
        ?.from('alert_agent_assignments')
        ?.insert({
          alert_id: alertId,
          agent_id: agentId,
          assignment_type: 'manual'
        })
        ?.select()
        ?.single();

      if (assignError) throw assignError;

      // Update alert status
      const { error: updateError } = await supabase
        ?.from('project_alerts')
        ?.update({ status: 'assigned' })
        ?.eq('id', alertId);

      if (updateError) throw updateError;

      return assignment;
    } catch (error) {
      console.error('Error manually assigning alert:', error);
      throw error;
    }
  },

  // Subscribe to real-time alerts
  subscribeToAlerts(callback) {
    const channel = supabase?.channel('alerts-changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_alerts'
        },
        async (payload) => {
          // Fetch full alert data with relationships
          if (payload?.new?.id) {
            const { data } = await supabase
              ?.from('project_alerts')
              ?.select(`
                *,
                project:chat_projects(id, name),
                assignments:alert_agent_assignments(
                  id,
                  agent:agents(id, name, role)
                )
              `)
              ?.eq('id', payload?.new?.id)
              ?.single();

            callback({
              event: payload?.eventType,
              alert: data
            });
          }
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to agent assignments
  subscribeToAssignments(callback) {
    const channel = supabase?.channel('assignments-changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'alert_agent_assignments'
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            assignment: payload?.new || payload?.old
          });
        }
      )?.subscribe();

    return channel;
  },

  // Get alert metrics for project
  async getAlertMetrics(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('alert_metrics')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('timestamp', { ascending: false })
        ?.limit(50);

      if (error) throw error;

      return data?.map(metric => ({
        id: metric?.id,
        projectId: metric?.project_id,
        metricName: metric?.metric_name,
        metricValue: metric?.metric_value,
        thresholdMin: metric?.threshold_min,
        thresholdMax: metric?.threshold_max,
        unit: metric?.unit,
        timestamp: metric?.timestamp,
        metadata: metric?.metadata
      })) || [];
    } catch (error) {
      console.error('Error fetching alert metrics:', error);
      throw error;
    }
  }
};